import api from './api';
import {
  Ticket,
  TicketStage,
  TicketReason,
  TicketMessage,
  CreateTicketDTO,
  UpdateTicketDTO,
  CreateTicketStageDTO,
  UpdateTicketStageDTO,
  CreateTicketReasonDTO,
  UpdateTicketReasonDTO,
  CreateTicketMessageDTO,
  TicketChecklistDTO,
  TicketMetrics,
  TicketNotificationItem
} from '../types/tickets';

export const ticketService = {
  // Stages
  async listStages(): Promise<TicketStage[]> {
    const response: any = await api.get('/tickets/stages');
    return response.stages || response.data?.stages || [];
  },

  async createStage(data: CreateTicketStageDTO): Promise<TicketStage> {
    const response: any = await api.post('/tickets/stages', data);
    return response.stage || response.data?.stage;
  },

  async updateStage(id: number, data: UpdateTicketStageDTO): Promise<TicketStage> {
    const response: any = await api.put(`/tickets/stages/${id}`, data);
    return response.stage || response.data?.stage;
  },

  async deleteStage(id: number): Promise<void> {
    await api.delete(`/tickets/stages/${id}`);
  },

  // Reasons
  async listReasons(): Promise<TicketReason[]> {
    const response: any = await api.get('/tickets/reasons');
    return response.reasons || response.data?.reasons || [];
  },

  async createReason(data: CreateTicketReasonDTO): Promise<TicketReason> {
    const response: any = await api.post('/tickets/reasons', data);
    return response.reason || response.data?.reason;
  },

  async updateReason(id: number, data: UpdateTicketReasonDTO): Promise<TicketReason> {
    const response: any = await api.put(`/tickets/reasons/${id}`, data);
    return response.reason || response.data?.reason;
  },

  async deleteReason(id: number): Promise<void> {
    await api.delete(`/tickets/reasons/${id}`);
  },

  // Tickets
  async kanban(status?: 'open'): Promise<TicketStage[]> {
    const params = status ? { status } : {};
    const response: any = await api.get('/tickets/kanban', { params });
    return response.stages || response.data?.stages || [];
  },

  async listTickets(params?: {
    client_id?: number;
    reason_id?: number;
    stage_id?: number;
    assigned_to?: number;
    status?: string;
    priority?: string;
    page?: number;
    limit?: number;
  }): Promise<{ tickets: Ticket[]; pagination: any }> {
    const response: any = await api.get('/tickets', { params });
    return {
      tickets: response.tickets || response.data?.tickets || [],
      pagination: response.pagination || response.data?.pagination || {}
    };
  },

  async getTicket(id: number): Promise<Ticket> {
    const response: any = await api.get(`/tickets/${id}`);
    return response.ticket || response.data?.ticket;
  },

  async createTicket(data: CreateTicketDTO): Promise<Ticket> {
    const response: any = await api.post('/tickets', data);
    return response.ticket || response.data?.ticket;
  },

  async updateTicket(id: number, data: UpdateTicketDTO): Promise<Ticket> {
    const response: any = await api.put(`/tickets/${id}`, data);
    return response.ticket || response.data?.ticket;
  },

  async moveTicketStage(id: number, stage_id: number, resolution_time_hours?: number): Promise<Ticket> {
    // Garantir que ambos os IDs sejam números
    const ticketId = Number(id);
    const stageIdNum = Number(stage_id);
    
    if (isNaN(ticketId) || isNaN(stageIdNum)) {
      throw new Error(`IDs inválidos: ticketId=${id}, stage_id=${stage_id}`);
    }
    
    const requestData: any = { stage_id: stageIdNum };
    if (resolution_time_hours !== undefined) {
      requestData.resolution_time_hours = resolution_time_hours;
    }
    
    console.log('📤 [ticketService] Movendo ticket:', {
      ticketId,
      stageId: stageIdNum,
      resolution_time_hours,
      requestData
    });
    
    const response: any = await api.put(`/tickets/${ticketId}/move-stage`, requestData);
    
    console.log('📥 [ticketService] Resposta recebida:', {
      hasTicket: !!response.ticket,
      hasData: !!response.data,
      ticket: response.ticket ? {
        id: response.ticket.id,
        stage_id: response.ticket.stage_id,
        stageIdType: typeof response.ticket.stage_id
      } : null
    });
    
    const ticket = response.ticket || response.data?.ticket;
    if (!ticket) {
      throw new Error('Resposta inválida do servidor');
    }
    
    // Normalizar o ticket retornado
    return {
      ...ticket,
      id: Number(ticket.id),
      stage_id: Number(ticket.stage_id) || ticket.stage_id
    };
  },

  async updateChecklist(id: number, data: TicketChecklistDTO): Promise<Ticket> {
    const response: any = await api.put(`/tickets/${id}/checklist`, data);
    return response.ticket || response.data?.ticket;
  },

  async deleteTicket(id: number): Promise<void> {
    await api.delete(`/tickets/${id}`);
  },

  // Messages
  async createMessage(ticketId: number, data: CreateTicketMessageDTO): Promise<TicketMessage> {
    const response: any = await api.post(`/tickets/${ticketId}/messages`, data);
    return response.message || response.data?.message;
  },

  async updateMessage(ticketId: number, messageId: number, content: string): Promise<TicketMessage> {
    const response: any = await api.put(`/tickets/${ticketId}/messages/${messageId}`, { content });
    return response.message || response.data?.message;
  },

  async deleteMessage(ticketId: number, messageId: number): Promise<void> {
    await api.delete(`/tickets/${ticketId}/messages/${messageId}`);
  },

  // Attachments (upload de mídia)
  async uploadAttachment(
    ticketId: number,
    file: File,
    opts?: { content?: string; asMessage?: boolean }
  ): Promise<{ message?: TicketMessage; attachment: any }> {
    const formData = new FormData();
    formData.append('file', file);
    if (opts?.content) formData.append('content', opts.content);
    // asMessage padrão = true; só envia 'false' quando explicitamente anexo de criação
    formData.append('as_message', opts?.asMessage === false ? 'false' : 'true');
    const response: any = await api.post(`/tickets/${ticketId}/attachments`, formData);
    return {
      message: response.message || response.data?.message,
      attachment: response.attachment || response.data?.attachment
    };
  },

  // Reports
  async getMetrics(params?: {
    start_date?: string;
    end_date?: string;
    reason_id?: number;
    client_id?: number;
  }): Promise<TicketMetrics> {
    const response: any = await api.get('/tickets/reports/metrics', { params });
    return response.metrics || response.data?.metrics;
  },

  // Notifications
  async getNotifications(): Promise<{ items: TicketNotificationItem[]; total: number }> {
    const response: any = await api.get('/tickets/notifications');
    return {
      items: response.items || response.data?.items || [],
      total: response.total ?? response.data?.total ?? 0,
    };
  },

  async markAllNotificationsRead(): Promise<void> {
    await api.post('/tickets/notifications/read-all');
  },

  async getUnviewedCount(): Promise<number> {
    const response: any = await api.get('/tickets/notifications/count');
    return response.count || 0;
  },

  async markAsViewed(ticketId: number): Promise<void> {
    await api.post(`/tickets/${ticketId}/view`);
  },

  async renewResolutionTime(id: number, resolution_time_hours: number): Promise<Ticket> {
    const response: any = await api.put(`/tickets/${id}/renew-resolution-time`, {
      resolution_time_hours
    });
    return response.ticket || response.data?.ticket;
  }
};

